/**
 * Supabase surfaces two error shapes:
 *   1. PostgrestError — { code: '42501', message, details, hint } where `code`
 *      is a 5-char SQLSTATE. The classes we care about:
 *        - Class 22 = data exception (e.g. 22P02 invalid_text_representation) — don't retry.
 *        - Class 23 = integrity constraint violation (e.g. 23505 unique_violation) — don't retry.
 *        - Class 42 = syntax error / access rule violation (includes 42501
 *          insufficient_privilege, 42P01 undefined_table) — don't retry.
 *        - Class 44 = with check option violation — don't retry.
 *      NOTE: Class 40 (transaction rollback) is DELIBERATELY allowed through.
 *      It contains the canonical retryable Postgres codes: 40001
 *      serialization_failure, 40P01 deadlock_detected, 40003
 *      statement_completion_unknown. These are transient and should be retried.
 *   2. AuthError / FunctionsHttpError — { status: 401, statusCode?: 401, name,
 *      message } where status is a plain HTTP code.
 *
 * We do NOT retry on any client-side (4xx) HTTP error or on any SQLSTATE in
 * classes 22 / 23 / 42 / 44 — those are deterministic and will keep failing.
 * Returning false also short-circuits the default exponential backoff.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  const err = (error ?? {}) as {
    code?: unknown;
    status?: unknown;
    statusCode?: unknown;
  };
  const code = typeof err.code === 'string' ? err.code : undefined;
  const status =
    typeof err.status === 'number'
      ? err.status
      : typeof err.statusCode === 'number'
        ? err.statusCode
        : undefined;

  // PostgrestError SQLSTATE terminal classes: 22 (data exception),
  // 23 (integrity constraint violation), 42 (syntax / access rule — incl.
  // 42501 insufficient_privilege), 44 (with check option violation).
  // Class 40 (transaction rollback — 40001 / 40P01 / 40003) is intentionally
  // excluded because those codes are transient and SHOULD be retried.
  if (code && /^(22|23|42|44)/.test(code)) return false;

  // HTTP 4xx from auth / edge functions / REST — client bug, don't retry.
  if (status !== undefined && status >= 400 && status < 500) return false;

  return failureCount < 2;
}
